import { DASH, M3U8, MP4, WEBM } from 'constants/mimes';
import {
  MIN_PLAY_RANGE_DURATION,
  PLAY_RANGE_NUDGE_AMOUNT,
} from 'constants/trimming';
import { getDashPlaylistWithCredentials } from 'utilities/dash';
import { getHlsPlaylistWithCredentials } from 'utilities/hls';
import * as logger from 'utilities/loggerx';
import * as videoUtils from 'utilities/video';

import { PlayRange } from 'types/playRanges';

import { SelectedPlayRangeHandleState } from '../clips/state';
import { NUDGE_BACKWARD, NudgeDirection } from '../hooks';
import { SECTION_HANDLE_LEFT } from '../section-handle';

interface VideoPlayerSourceProperties {
  source_url: string | null;
  source_mime_type: string | null;
  source_url_is_transcoded: boolean;
  // TODO (weave) change this type
  source_part_credentials: unknown;
}

export const getClipSourceUrl = async ({
  // @ts-expect-error ignore due to enabling strict null checks
  clip = null,
  forceOriginal = false,
}: {
  clip?: { id: string; root_video_id: string };
  forceOriginal?: boolean;
} = {}): Promise<VideoPlayerSourceProperties | undefined> => {
  try {
    // TODO (weave): When we update thumbnails then we can get the poster url
    // const posterUrl = videoUtils.getPosterUrl(video);

    if (!clip) {
      return;
    }

    const clipId = clip.id;

    const { partCredentials, sourceMimeType, sourceUrl } = await new Promise(
      resolve => {
        let sourceMimeType;

        // TODO (weave): support multiple mimetyps when applicable
        // supportedMimeTypes = videoUtils.getSupportedMimeTypes();
        const supportedMimeTypes = [M3U8];

        const getUrlHandler = videoUtils.postSessionRawURL;

        getUrlHandler(
          clip.root_video_id,
          // TODO (weave): figure out password to pass in
          null,
          (sourceUrl, partCredentials = {}) => {
            const { pathname } = new URL(sourceUrl.toLowerCase());

            if (pathname.endsWith('.mp4')) {
              sourceMimeType = MP4;
            } else if (pathname.endsWith('.webm')) {
              sourceMimeType = WEBM;
            } else if (pathname.endsWith('.m3u8')) {
              sourceMimeType = M3U8;
            } else if (pathname.endsWith('.mpd')) {
              sourceMimeType = DASH;
            }

            resolve({ partCredentials, sourceMimeType, sourceUrl });
          },
          forceOriginal,
          supportedMimeTypes,
          clipId
        );
      }
    );

    const finalSourceUrl = await (() => {
      if (Object.keys(partCredentials).length === 0) {
        return sourceUrl;
      }

      if (sourceMimeType === M3U8) {
        // take the m3u8 playlist url, download it, and then use the part credentials
        // to create a blob URL of a modified playlist with the credentials attached
        // returns base64 encoded url in staging and production
        return getHlsPlaylistWithCredentials(sourceUrl, partCredentials);
      } else if (sourceMimeType === DASH) {
        return getDashPlaylistWithCredentials(sourceUrl, partCredentials);
      }

      return sourceUrl;
    })();

    // TODO (Weave): When we update thumbnails then we can get the poster url
    // if (posterUrl) {
    //   dispatch(updateVideoPlayerPosterUrl(posterUrl));
    // }

    const sourceUrlIsTranscoded = sourceUrl.includes('/sessions/transcoded/');

    return {
      source_url: finalSourceUrl,
      source_mime_type: sourceMimeType,
      source_part_credentials: partCredentials,
      source_url_is_transcoded: sourceUrlIsTranscoded,
    };
  } catch (err) {
    logger.error(
      err,
      { message: `Error retrieving source url for clip id ${clip.id}` },
      { team: logger.CREATE_TEAM, clipId: clip.id, videoId: clip.root_video_id }
    );
  }
};

export const findClosestPlayRangeAndLoc = (
  time: number,
  playRanges: PlayRange[]
): { closestLoc: number; playRangeIdx: number } => {
  let playRangeIdx: number;
  let closestLoc: number;
  let distance = Infinity;

  for (let idx = 0; idx < playRanges.length; idx++) {
    const range = playRanges[idx];

    if (Math.abs(range.from - time) < distance) {
      closestLoc = range.from;
      playRangeIdx = idx;
      distance = Math.abs(range.from - time);
    }

    if (Math.abs(range.to - time) < distance) {
      closestLoc = range.to;
      playRangeIdx = idx;
      distance = Math.abs(range.to - time);
    }
  }

  // @ts-expect-error ignore due to enabling strict null checks
  return { closestLoc, playRangeIdx };
};

export const getNextTrimmedPlayRangeValues = (
  currentClipPlayRanges: PlayRange[],
  selectedHandleSide: SelectedPlayRangeHandleState,
  nudgeDirection: NudgeDirection,
  focusedPlayRangeIndex: number,
  duration?: number,
  currentPlayRange?: PlayRange
): PlayRange | null => {
  if (
    !currentClipPlayRanges.length ||
    !currentPlayRange ||
    !duration ||
    !selectedHandleSide
  ) {
    return null;
  }

  const { from, id, to } = currentPlayRange;

  const fromValuePlusMinDurationGap = from + MIN_PLAY_RANGE_DURATION;
  const toValueMinusMinDurationGap = to - MIN_PLAY_RANGE_DURATION;

  const previousPlayRangeToValue =
    focusedPlayRangeIndex <= 0
      ? 0
      : currentClipPlayRanges[focusedPlayRangeIndex - 1].to;
  const nextPlayRangeFromValue =
    focusedPlayRangeIndex === currentClipPlayRanges.length - 1
      ? duration
      : currentClipPlayRanges[focusedPlayRangeIndex + 1].from;

  const isLeftHandleSelected = selectedHandleSide === SECTION_HANDLE_LEFT;
  const isNudgeBackward = nudgeDirection === NUDGE_BACKWARD;

  // Handle case of moving left play range handle backward
  if (isLeftHandleSelected && isNudgeBackward) {
    const initialFromValue = from - PLAY_RANGE_NUDGE_AMOUNT;

    return {
      from:
        initialFromValue >= previousPlayRangeToValue
          ? initialFromValue
          : previousPlayRangeToValue,
      id,
      to,
    };
  }

  // Handle case of moving left play range handle forward
  else if (isLeftHandleSelected) {
    const initialFromValue = from + PLAY_RANGE_NUDGE_AMOUNT;

    return {
      from:
        initialFromValue <= toValueMinusMinDurationGap
          ? initialFromValue
          : toValueMinusMinDurationGap,
      id,
      to,
    };
  }

  // Handle case of moving right play range handle backward
  else if (isNudgeBackward) {
    const initialToValue = to - PLAY_RANGE_NUDGE_AMOUNT;

    return {
      from,
      id,
      to:
        initialToValue >= fromValuePlusMinDurationGap
          ? initialToValue
          : fromValuePlusMinDurationGap,
    };
  }

  // Handle case of moving right play range handle forward
  const initialToValue = to + PLAY_RANGE_NUDGE_AMOUNT;

  return {
    from,
    id,
    to:
      initialToValue <= nextPlayRangeFromValue
        ? initialToValue
        : nextPlayRangeFromValue,
  };
};
